/** Пустое значение по правилам интерфейса разделов Адреса / Комплексы */
export const EMPTY_ADDRESS = "Н/Д";

const asTrimmed = (value) => {
  if (value == null) return "";
  const str = String(value).trim();
  return str;
};

/** Есть ли непустой address_id / address_cam_id */
export const hasAddressLinkId = (value) => asTrimmed(value) !== "";

/**
 * Текст адреса комплекса: new_address.address (плоско или вложенно).
 * `location` — то же поле, по которому фильтрует /operator/sensors.
 */
const resolveComplexAddressText = (item) =>
  asTrimmed(
    item?.address ??
      item?.new_address?.address ??
      (typeof item?.new_address === "string" ? item.new_address : null) ??
      item?.location
  ) || null;

/**
 * Текст адреса камеры: new_address_cam.address_cam (плоско или вложенно).
 * `location_cam` — поле камеры в sensor_cameras (/operator/sensors).
 */
const resolveCameraAddressText = (camera) => {
  const candidates = [
    camera?.address_cam,
    camera?.new_address_cam?.address_cam,
    typeof camera?.new_address_cam === "string" ? camera.new_address_cam : null,
    camera?.location_cam,
    camera?.location,
  ];
  for (const value of candidates) {
    const text = asTrimmed(value);
    if (text) return text;
  }
  return null;
};

/**
 * Местоположение комплекса: нужен address_id, текст — из new_address.address / address / location.
 * Без ID не показываем подпись (не подставляем «голый» текст без связи с Адресами).
 */
export const getComplexLocationLabel = (item) => {
  if (!item || typeof item !== "object") return null;
  if (!getComplexAddressId(item)) return null;
  return resolveComplexAddressText(item);
};

/**
 * Местоположение камеры.
 * Со связкой address_cam_id — текст адреса из Адресов, иначе location_cam.
 */
export const getCameraLocationLabel = (camera) => {
  if (!camera || typeof camera !== "object") return null;
  if (getCameraAddressId(camera)) return resolveCameraAddressText(camera);
  return asTrimmed(camera?.location_cam) || null;
};

export const formatAddressDisplay = (value) => asTrimmed(value) || EMPTY_ADDRESS;

/** ID адреса комплекса (плоско address_id или new_address.address_id) */
export const getComplexAddressId = (item) => {
  const id = asTrimmed(item?.address_id ?? item?.new_address?.address_id);
  return id || null;
};

/** ID адреса камеры (плоско address_cam_id или new_address_cam.address_cam_id) */
export const getCameraAddressId = (camera) => {
  const id = asTrimmed(
    camera?.address_cam_id ?? camera?.new_address_cam?.address_cam_id
  );
  return id || null;
};

/**
 * Текст адреса в разделе «Адреса».
 * API `/operator/locations` отдаёт `location`.
 */
export const getLocationAddressText = (item) =>
  asTrimmed(
    item?.location ?? item?.address ?? item?.address_cam ?? item?.new_address?.address
  ) || null;

const isValidCoordinatePair = (lat, lng) => {
  if (lat == null || lng == null || asTrimmed(lat) === "" || asTrimmed(lng) === "") {
    return false;
  }
  const latNum = Number(lat);
  const lngNum = Number(lng);
  if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return false;
  // 0,0 в API означает отсутствие координат
  if (latNum === 0 && lngNum === 0) return false;
  return true;
};

/** Пары координат в порядке приоритета для ответа locations */
const getLocationCoordinatePairs = (item) => {
  if (!item || typeof item !== "object") return [];
  // Адрес-уровневые координаты (после группировки devices) — в приоритете
  return [
    [item.latitude_complex, item.longitude_complex],
    [item.latitude_address, item.longitude_address],
    [item.latitude_address_cam, item.longitude_address_cam],
    [item.latitude_cam, item.longitude_cam],
  ];
};

/** complex_cam / type → подпись «Комплекс» | «Камера» */
const resolveDeviceTypeLabel = (deviceOrItem) => {
  const cam = asTrimmed(deviceOrItem?.complex_cam);
  if (cam === "camera") return "Камера";
  if (cam === "sensor") return "Комплекс";
  const type = asTrimmed(deviceOrItem?.type);
  if (type === "Камера" || type === "camera") return "Камера";
  if (type === "Комплекс" || type === "sensor") return "Комплекс";
  if (type) return type;
  return "Комплекс";
};

/** Устройства на одном адресе (новый формат API) + fallback на плоские поля */
export const getLocationDevices = (item) => {
  if (!item || typeof item !== "object") return [];
  if (Array.isArray(item.devices) && item.devices.length > 0) {
    return item.devices.map((device) => ({
      id: asTrimmed(device?.id) || null,
      // API: complex_serial / complex_cam (rename serial/type отвергли)
      serial: asTrimmed(device?.complex_serial ?? device?.serial),
      type: resolveDeviceTypeLabel(device),
      complex_cam: asTrimmed(device?.complex_cam),
      class: asTrimmed(device?.class),
    }));
  }
  const serial = asTrimmed(item.complex_serial);
  const type = resolveDeviceTypeLabel(item);
  const deviceClass = asTrimmed(item.class);
  if (!serial && !deviceClass && !item.complex_cam && !item.type) return [];
  return [
    {
      id: asTrimmed(item.complex_id) || asTrimmed(item.id) || null,
      serial,
      type,
      complex_cam: asTrimmed(item.complex_cam),
      class: deviceClass,
    },
  ];
};

/** Типы комплексов для колонки «Тип комплекса» (схлапывание с бэка через classes) */
export const getLocationClasses = (item) => {
  if (Array.isArray(item?.classes) && item.classes.length > 0) {
    return item.classes.map((value) => asTrimmed(value)).filter(Boolean);
  }
  const deviceClasses = getLocationDevices(item)
    .map((device) => device.class)
    .filter(Boolean);
  if (deviceClasses.length === 0) {
    const fallback = asTrimmed(item?.class);
    return fallback ? [fallback] : [];
  }
  if (deviceClasses.every((value) => value === deviceClasses[0])) {
    return [deviceClasses[0]];
  }
  return deviceClasses;
};

export const getLocationEntityTypeLabels = (item) => {
  const devices = getLocationDevices(item);
  if (devices.length === 0) return [];
  return devices.map((device) => {
    if (device.type) return device.type;
    return "Комплекс";
  });
};

export const getLocationSerialLabels = (item) =>
  getLocationDevices(item).map((device) => device.serial || EMPTY_ADDRESS);

export const getLocationLatitude = (item) => {
  for (const [lat, lng] of getLocationCoordinatePairs(item)) {
    if (isValidCoordinatePair(lat, lng)) return lat;
  }
  return null;
};

export const getLocationLongitude = (item) => {
  for (const [lat, lng] of getLocationCoordinatePairs(item)) {
    if (isValidCoordinatePair(lat, lng)) return lng;
  }
  return null;
};

/** Пустые / нулевые координаты — тире */
export const getLocationCoordinatesLabel = (item) => {
  for (const [lat, lng] of getLocationCoordinatePairs(item)) {
    if (isValidCoordinatePair(lat, lng)) return `${lat}, ${lng}`;
  }
  return "—";
};

export const getLocationEntityTypeLabel = (item) => {
  const labels = getLocationEntityTypeLabels(item);
  if (labels.length > 0) return labels.join("\n");
  return item?.complex_cam === "camera" ? "Камера" : "Комплекс";
};

export const getLocationRecordId = (item) => {
  const id = asTrimmed(
    item?.address_id ?? item?.address_cam_id ?? item?.id
  );
  return id || null;
};

const EMPTY_DASH = "—";

const isPlaceholderDate = (value) => {
  const raw = asTrimmed(value);
  if (!raw) return true;
  // Нулевая / sentinel-дата из API (go zero time и аналоги)
  if (/^0001-01-01/.test(raw) || /^0000-00-00/.test(raw)) return true;
  if (raw === "01.01.0001" || raw === "00.00.0000") return true;
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) {
    const year = Number(match[1]);
    if (!Number.isFinite(year) || year < 1900) return true;
  }
  return false;
};

export const formatLocationFieldValue = (value, { date = false } = {}) => {
  const raw = asTrimmed(value);
  if (!raw) return date ? EMPTY_DASH : EMPTY_ADDRESS;
  if (!date) return raw;
  if (isPlaceholderDate(raw)) return EMPTY_DASH;
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match) return `${match[3]}.${match[2]}.${match[1]}`;
  return raw;
};
